(ns app.front.view.group.admin.inspections.template-editor
  (:require [app.domain.group.permit-type-config.model :as ptc-model]
            [app.domain.inspection.model :as inspection-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.components :as uic]
            [app.front.ui.config-pills :as ui-config-pills]
            [app.front.ui.dnd :as dnd]
            [app.front.ui.i18n :as ui-i18n]
            [app.front.ui.multi-select-tags :as ui-mst]
            [app.front.ui.select :as ui-select]
            [app.front.ui.table :as ui-table]
            [app.front.ui.tags :as ui-tags]
            [app.front.ui.text-editor :as text-editor]
            [app.front.util :as futil]
            [app.front.view.group.admin.common :as acommon]
            [app.front.view.group.admin.inspections.subs :as ias]
            [app.front.view.group.admin.subs :as gas]
            [app.front.view.inspections.util :as inspection-util]
            [app.i18n.model :as i18n-model]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.db.entity.util :as eu]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(def temp-id -1)

;;; Utilities
;;;;;;;;;;;;;
(defn ->id [{:db/keys [id]} _] id)

(defn a-if-same [{id-a :db/id :as entity-a} {id-b :db/id :as entity-b}]
  (if (= id-a id-b)
    entity-a
    entity-b))

(defn replace-entity
  "Given entity `e`, replace that entity from a collection.

  If `e` is nil, does nothing. If `e` has no `:db/id`, appends."
  [{:db/keys [id] :as entity} all]
  (cond
    (not= id temp-id) (map (partial a-if-same entity) all)
    entity (conj all entity)
    :else all))

(defn editor-props [tr id key disp-on-change & [coercer]]
  {:id         (-> key name (str id))
   :label-text (tr [key])
   :required   true
   :on-change  #(rf/dispatch-sync [disp-on-change id key (-> % futil/tval ((or coercer identity)))])})

(def template-kind-submit-keys
  (conj inspection-model/template-kind-editable-keys
        :db/id))

(def new-template-kind-submit-keys inspection-model/template-kind-editable-keys)

(def template-submit-keys
  (conj inspection-model/template-editable-keys
        :db/id))

(defn active-templates-by-template-kind-ids [ctx kind-ids]
  (->> (entities/find-entities ctx (filter #(-> % :entity/parent kind-ids)))
       (filter (eu/is-type-fn :entity.type/inspection-template))
       (filter du/is-active?)))

(defn template-kind-visit-results [template-kind]
  (some-> template-kind
          :inspection.template.kind/visit-type
          :inspection-visit-type/visit-results))

;;; Events
;;;;;;;;;;
(rf/reg-event-db ::add-new-template-kind
  (fn [db [_ order multiple-bookings?]]
    (-> db
        (assoc ::current-kind (cond-> {:db/id                                                 temp-id
                                       :ordered/order                                         order
                                       :inspection.template.kind/multiple-applicant-bookings? multiple-bookings?}
                                (ff/enabled? :feature-flag.id/pre-issuance-inspections)
                                (assoc :inspection.template.kind/phases
                                       inspection-model/default-inspection-template-kind-phases))))))



(defn handle-select-for-edit [db key keys-to-include i18n-keys item]
  (let [item (when item
               (reduce (fn [item [i18n-attr fallback-attr]]
                         (if (i18n-attr item)
                           (update item i18n-attr i18n-model/strings-map)
                           (assoc item i18n-attr {i18n-model/default-lang (fallback-attr item)})))
                       (-> item
                           (select-keys (concat keys-to-include
                                                (mapcat identity i18n-keys))))
                       i18n-keys))]
    (assoc db key item)))


(defn automatic-selection-of-template-for-edit [db template-kind]
  (let [template-entities (active-templates-by-template-kind-ids (entities/db->context db)
                                                                 #{(:db/id template-kind)})]
    (if (= 1 (count template-entities))
      (handle-select-for-edit db
                              ::current-template
                              template-submit-keys
                              nil
                              (first template-entities))
      (dissoc db ::current-template))))

(rf/reg-event-db ::select-kind-for-edit
  (fn [db [_ kinds kind-id]]
    (let [kind (when (not= kind-id (-> db ::current-kind :db/id))
                 (p/find-first kinds {:db/id kind-id}))]
      (-> db
          (handle-select-for-edit ::current-kind
                                  (conj template-kind-submit-keys
                                        :ordered/order)
                                  [[:inspection.template.kind/names :inspection.template.kind/name]
                                   [:inspection.template.kind/descriptions :inspection.template.kind/description]]
                                  kind)
          (automatic-selection-of-template-for-edit kind)))))


(defn set-value-fn [key]
  (fn [db [_ id path value]]
    (if (= id (-> db key :db/id))
      (let [path (if (vector? path) path [path])]
        (assoc-in db (cons key path) value))
      ;; don't update if entity-under-edit is different
      db)))

(rf/reg-event-db ::set-kind-value
  (set-value-fn ::current-kind))

(rf/reg-sub ::get-kind-value
  (fn [db [_ path]]
    (get-in db (cons ::current-kind (if (vector? path) path [path])))))


(defn save-value-handler-fn [command]
  (fn [_ [_ id path value]]
    (when (not= id temp-id)
      {:comm/command {:command    command
                      :data       {:id    id
                                   :attr  path
                                   :value value}
                      :on-success [::entities/apply-changes]
                      :notify     {:success :changes-saved}}})))

(rf/reg-event-fx ::save-value-kind
  (save-value-handler-fn :inspection/edit-template-kind))


(defn save-handler-fn [command link-to-id-key submit-keys on-success]
  (fn [_ [_ link-to-id data]]
    {:comm/command {:command    command
                    :data       {link-to-id-key link-to-id
                                 :data          (select-keys data
                                                             submit-keys)}
                    :on-success [on-success]}}))

(defn save-done-handler-fn [then-dissoc]
  (fn [{:keys [db]} [_ status changes]]
    {:db       (dissoc db then-dissoc)
     :dispatch [::entities/apply-changes status changes]}))

(rf/reg-event-fx ::save-new-kind
  (save-handler-fn :inspection/create-template-kind
                   :authority-group-id
                   new-template-kind-submit-keys
                   ::save-new-done-kind))


(rf/reg-event-fx ::save-new-done-kind
  (save-done-handler-fn ::current-kind))


(rf/reg-event-fx ::reorder
  (fn [_ [_ group-id new-order]]
    {:comm/command {:command    :inspection/reorder
                    :data       {:reorder-kinds      new-order
                                 :authority-group-id group-id}
                    :on-success [::entities/apply-changes]}}))

(rf/reg-event-fx ::remove-template-kind-dependency
  (fn [_ [_ authority-group-id inspection-template-kind-id dependency-id]]
    {:comm/command {:command    :inspection/remove-template-kind-dependency
                    :data       (util/mmap authority-group-id
                                           inspection-template-kind-id
                                           dependency-id)
                    :on-success [::entities/apply-changes]}}))

;;; Subscriptions
;;;;;;;;;;;;;;;;;

;; The template that is currently being edited
(rf/reg-sub ::current-kind (fn [{::keys [current-kind]} _] current-kind))
(rf/reg-sub ::current-template (fn [{::keys [current-template]} _] current-template))

(rf/reg-sub ::template-kinds
  :<- [::gas/template-kind-entities]
  :<- [::current-kind]
  (fn [[all current] _]
    (->> all
         (replace-entity current)
         (sort-by :ordered/order))))

(rf/reg-sub ::template-kind-ids
  :<- [::gas/template-kind-entities]
  :-> du/entity-id-set)

(rf/reg-sub ::template-entities
  :<- [::entities/context]
  :<- [::template-kind-ids]
  (fn [[ctx kind-ids] _]
    (active-templates-by-template-kind-ids ctx kind-ids)))

(rf/reg-sub ::templates
  :<- [::template-entities]
  :<- [::current-template]
  (fn [[all current] [_ template-kind-id]]
    (->> all
         (filter #(-> % :entity/parent :db/id (= template-kind-id)))
         (replace-entity current)
         (sort-by :db/id du/compare-nil-last)
         seq)))


(rf/reg-sub ::n-templates
  (fn [[_ template-kind-id]]
    (rf/subscribe [::templates template-kind-id]))
  :-> count)

(rf/reg-sub ::no-templates-for-template-kind
  (fn [[_ template-kind-id]]
    (rf/subscribe [::n-templates template-kind-id]))
  (fn [n-templates [_ template-kind-id]]
    (and (not= template-kind-id temp-id) (= n-templates 0))))


(rf/reg-sub ::order-for-last
  :<- [::template-kinds]
  (fn [template-kinds]
    (-> template-kinds
        last
        :ordered/order
        inc)))

(rf/reg-sub ::id-of-current-kind
  :<- [::current-kind]
  ->id)

(rf/reg-sub ::expanded-kinds
  :<- [::current-kind]
  #(if %
     #{(:db/id %)}
     #{}))


(rf/reg-sub ::adding-new?
  :<- [::current-kind]
  :<- [::current-template]
  (fn [currents _]
    (some (fn [{id :db/id :as current}]
            (and (-> current nil? not)
                 (= id temp-id)))
          currents)))

(rf/reg-sub ::save-new-template-disabled?
  :<- [::current-kind]
  (fn [{:inspection.template.kind/keys [names descriptions visit-type subtype]}]
    (or (i18n-model/blank? names)
        (i18n-model/blank? descriptions)
        (and (nil? visit-type)
             (nil? subtype)))))

;;; Components
;;;;;;;;;;;;;;
(defn simple-editor [{:keys [set-value-event save-value-event]}
                     {:keys [tr item id required?]}
                     component
                     attr
                     & [coercer]]
  (let [c           (if (vector? component) (first component) component)
        extra-props (if (vector? component) (second component) {})]
    [c
     (merge (editor-props tr id attr set-value-event coercer)
            {:value    (get item attr)
             :required required?
             :on-blur  #(>evt [save-value-event
                               id
                               attr
                               (-> %
                                   futil/tval
                                   ((or coercer identity)))])}
            extra-props)]))



(def simple-editor-for-kind
  (partial simple-editor {:set-value-event  ::set-kind-value
                          :save-value-event ::save-value-kind}))

(defn i18n-editor-for-kind [{:keys [tr required? id item multiline?]} attr]
  [ui-i18n/localized-string-edit
   {:on-change           #(rf/dispatch-sync [::set-kind-value id attr %])
    :on-end-edit         #(>evt [::save-value-kind id attr %])
    :strings             (get item attr)
    :label-text          (tr [attr])
    :required            required?
    :multiline?          multiline?
    :allowed-locales-sub ::gas/allowed-locales}])


(defn visit-type-selector [{:keys [tr authority-group-id]} visit-type save-value-fn get-component-id lang]
  (let [visit-types (<sub [::ias/visit-type-entities authority-group-id])]
    [uic/form-field
     [ui-select/select
      {:values       (map :db/id visit-types)
       :id           (get-component-id "subtype-select")
       :label-text   (tr [:inspection.template.kind/visit-type])
       :val->str     #(-> (p/find-first visit-types {:db/id %})
                          (inspection-model/visit-type-name lang))
       :on-change    #(save-value-fn :inspection.template.kind/visit-type %)
       :value        (:db/id visit-type visit-type)
       :allow-empty? false
       :required     true}]]))


(defn qualification-selector-v2 [tr {id :db/id :as template-kind} save-value-fn]
  [:div.mt-3
   (let [lang           (<sub [:language])
         qualifications (<sub [::ias/enabled-qualifications])
         selected       (->> template-kind :inspection.template.kind/inspector-qualifications (map #(:db/id % %)) set)
         label-text     (str (tr [:inspection.template/qualification]) " " (count selected) "/" (count qualifications))
         on-change      (fn [operation]
                          (fn [v]
                            (let [new-qs (operation v)]
                              (save-value-fn :inspection.template.kind/inspector-qualifications (set new-qs)))))]
     [ui-config-pills/config-pills
      (util/mmap {:id              (str id "-qualification-select")
                  :val->str        (fn [id]
                                     (-> (p/find-first qualifications {:db/id id})
                                         (inspection-model/inspector-qualification-name lang)))
                  :add-button-text (tr [:inspection/add-requirement])
                  :selected        selected
                  :legend-text     label-text
                  :add-item        (on-change #(conj selected %))
                  :remove-item     (on-change #(disj selected %))
                  :values          (map :db/id qualifications)}
                 tr)])])

(defn inspection-dependency-template-kind-selector [{:keys [tr template-kind-id]}]
  (let [pre-issuance-inspections? (<sub [::ff/enabled? :feature-flag.id/pre-issuance-inspections])
        current-kind              (<sub [::current-kind])
        has-phases?               (or (not pre-issuance-inspections?)
                                      (seq (:inspection.template.kind/phases current-kind)))
        selected-template-kind    (<sub [::selected-dependency-template-kind template-kind-id])
        lang                      (<sub [:language])]
    [:div.flex-1
     (if has-phases?
       [ui-select/select
        {:values       (<sub [::available-template-kinds template-kind-id])
         :value        selected-template-kind
         :val->str     #(inspection-model/inspection-template-kind-name % lang)
         :on-change    #(>evt [::set-selected-dependency-template-kind template-kind-id %])
         :label-text   (tr [:inspection.dependencies/required-inspection-template-kind])
         :id           "current-dependency-template-kind"
         :allow-empty? true
         :placeholder  (tr [:inspection.dependencies/select-inspection-type])}]
       [:div.bx--form-item
        [:div.bx--label (tr [:inspection.dependencies/required-inspection-template-kind])]
        [:div.text-sm.text-gray-600.italic
         (tr [:inspection.dependencies/complete-phase-selection])]])]))

(defn inspection-dependency-mandatory-results-display [{:keys [tr template-kind-id]}]
  (let [selected-template-kind (<sub [::selected-dependency-template-kind template-kind-id])
        mandatory-results      (<sub [::mandatory-visit-results template-kind-id])
        lang                   (<sub [:language])]
    [:div.flex-1
     (if selected-template-kind
       [:div
        [:div.bx--label.text-sm.text-gray-600.mb-2
         (tr [:inspection.dependencies/mandatory-visit-results])]
        [:div.flex.flex-wrap.gap-1
         (for [result mandatory-results]
           ^{:key (:db/id result)}
           [ui-tags/tag {:label-text (inspection-model/visit-result-name result lang)}])]]
       [:div.bx--label.text-sm.text-gray-600
        (tr [:inspection.dependencies/mandatory-visit-results])])]))

(defn inspection-dependency-optional-results-selector [{:keys [tr template-kind-id]}]
  (let [selected-template-kind (<sub [::selected-dependency-template-kind template-kind-id])
        all-visit-results      (<sub [::template-kind-visit-results template-kind-id])
        mandatory-results      (<sub [::mandatory-visit-results template-kind-id])
        selected-results       (<sub [::selected-dependency-visit-results template-kind-id])
        lang                   (<sub [:language])]
    [:div.flex-1
     (if selected-template-kind
       ^{:key (str "visit-results-" template-kind-id "-" (hash selected-results))}
       [ui-mst/multi-select-tags
        {:values      (remove (set mandatory-results) all-visit-results)
         :value       selected-results
         :val->str    #(inspection-model/visit-result-name % lang)
         :on-change   #(>evt [::set-selected-dependency-visit-results template-kind-id %])
         :label-text  (tr [:inspection.dependencies/optional-visit-results])
         :select-text (tr [:inspection.dependencies/select-visit-results])
         :tr          tr}]
       [:div.bx--form-item
        [:div.bx--label (tr [:inspection.dependencies/optional-visit-results])]
        [:div.bx--select.bx--select--disabled
         [:select.bx--select-input {:disabled true}
          [:option (tr [:inspection.dependencies/select-visit-results])]]]])]))

(defn inspection-dependency-add-button [{:keys [tr template-kind-id authority-group-id]}]
  (let [selected-template-kind (<sub [::selected-dependency-template-kind template-kind-id])
        selected-results       (<sub [::selected-dependency-visit-results template-kind-id])
        mandatory-results      (<sub [::mandatory-visit-results template-kind-id])]
    [:div.flex.flex-col.justify-end.h-full
     [ui/button
      {:kind          :primary
       :size          :small
       :disabled      (not (and selected-template-kind
                                (or (seq selected-results) (seq mandatory-results))))
       :on-click      #(>evt [::add-template-kind-dependency template-kind-id authority-group-id])
       :icon          i/plus
       :icon-position :right}
      (tr [:add])]]))

(defn inspection-dependencies-add [{:keys [tr template-kind-id authority-group-id]}]
  [:div.mt-6.border-t.pt-6
   [:h3.text-lg.font-medium.mb-4
    (tr [:inspection.dependencies/title])]

   [:div.flex.items-start.gap-4.p-4.border.border-gray-200.rounded.bg-gray-50.mb-4
    [inspection-dependency-template-kind-selector {:tr               tr
                                                   :template-kind-id template-kind-id}]
    [inspection-dependency-mandatory-results-display {:tr               tr
                                                      :template-kind-id template-kind-id}]
    [inspection-dependency-optional-results-selector {:tr               tr
                                                      :template-kind-id template-kind-id}]
    [inspection-dependency-add-button {:tr                 tr
                                       :template-kind-id   template-kind-id
                                       :authority-group-id authority-group-id}]]])

(defn inspection-dependencies-list [{:keys [tr template-kind-id authority-group-id]}]
  (let [dependencies (<sub [::ias/inspection-dependencies template-kind-id])
        lang         (<sub [:language])]
    (when (seq dependencies)
      [:div.mt-4.mb-4
       [ui-table/modern-responsive-table
        {:cols    [[:inspection.dependencies/required-inspection-template-kind
                    (fn [{:inspection-dependency/keys [required-template-kind]}]
                      (some-> required-template-kind
                              (inspection-model/inspection-template-kind-name lang)))]
                   [:inspection.dependencies/required-visit-results
                    (fn [{:inspection-dependency/keys [required-visit-results]}]
                      (when (seq required-visit-results)
                        (->> required-visit-results
                             (map #(inspection-model/visit-result-name % lang))
                             (str/join ", "))))]
                   [""
                    (fn [dep]
                      [:div.flex.justify-end
                       [ui/button
                        {:kind     :ghost
                         :size     :small
                         :icon     i/trash
                         :on-click #(>evt [::remove-template-kind-dependency
                                           authority-group-id
                                           template-kind-id
                                           (:db/id dep)])}
                        (tr [:delete])]])]]
         :items   dependencies
         :tr      tr
         :id-func (fn [dep] (str (-> dep :inspection-dependency/required-template-kind :db/id) "-" (str/join "-" (sort (map :db/id (:inspection-dependency/required-visit-results dep))))))}]])))

(rf/reg-sub ::selected-dependency-template-kind
  (fn [db [_ template-kind-id]]
    (get-in db [::selected-dependency-template-kind template-kind-id])))

(rf/reg-sub ::selected-dependency-visit-results
  (fn [db [_ template-kind-id]]
    (get-in db [::selected-dependency-visit-results template-kind-id] #{})))

(rf/reg-sub ::saved-dependencies
  (fn [db [_ template-kind-id]]
    (get-in db [::saved-dependencies template-kind-id] [])))

(rf/reg-sub ::available-template-kinds
  :<- [::gas/template-kind-entities]
  :<- [:language]
  :<- [::current-kind]
  (fn [[template-kinds lang current-kind] [_ current-template-kind-id]]
    (let [current-phases (or (some-> current-kind
                                     inspection-model/inspection-template-kind-phases-or-default
                                     set)
                             #{})]
      (->> template-kinds
           (remove #(= (:db/id %) current-template-kind-id))
           (remove #(-> %
                        :inspection.template.kind/visit-type
                        :inspection-visit-type/workflow
                        (= :inspection.visit.type-workflow/final-inspection)))
           (filter #(seq (set/intersection current-phases
                                           (set (inspection-model/inspection-template-kind-phases-or-default %)))))
           (sort-by #(inspection-model/inspection-template-kind-name % lang))))))

(rf/reg-sub ::template-kind-visit-results
  (fn [[_ template-kind-id]]
    (rf/subscribe [::selected-dependency-template-kind template-kind-id]))
  :-> template-kind-visit-results)

(rf/reg-sub ::mandatory-visit-results
  (fn [[_ template-kind-id]]
    (rf/subscribe [::template-kind-visit-results template-kind-id]))
  :-> (partial filter #(inspection-model/visit-result-rule-set? % :inspection.visit.result-rule/close-inspection)))

(rf/reg-sub ::inspection-template-kind-has-dependencies?
  (fn [[_ template-kind-id]]
    (rf/subscribe [::ias/inspection-dependencies template-kind-id]))
  (fn [dependencies _]
    (boolean (seq dependencies))))

(rf/reg-sub ::dependent-template-kinds
  :<- [::gas/template-kind-entities]
  (fn [template-kinds [_ template-kind-id]]
    (->> template-kinds
         (filter (fn [template-kind]
                   (->> template-kind
                        :inspection.template.kind/dependencies
                        (some #(-> % :inspection-dependency/required-template-kind :db/id (= template-kind-id)))))))))

(rf/reg-event-db ::set-selected-dependency-template-kind
  (fn [db [_ template-kind-id new-template-kind]]
    (-> db
        (assoc-in [::selected-dependency-template-kind template-kind-id] new-template-kind)
        (assoc-in [::selected-dependency-visit-results template-kind-id] #{}))))

(rf/reg-event-db ::set-selected-dependency-visit-results
  (fn [db [_ template-kind-id new-results]]
    (assoc-in db [::selected-dependency-visit-results template-kind-id] (set new-results))))

(defn kind-editor-component-id [template-kind-id component-name]
  (str "pdf-template-editor-" component-name "-" template-kind-id))

(defn save-kind-attr [id attr value]
  (>evt [::set-kind-value id attr value])
  (>evt [::save-value-kind id attr value]))

(def template-kind-phase-options
  [:inspection.template.kind.phase/pre-issuance
   :inspection.template.kind.phase/post-issuance])

(defn template-kind-phase-label-key [phase]
  (case phase
    :inspection.template.kind.phase/pre-issuance :inspection.template.kind.phase/pre-issuance
    :inspection.template.kind.phase/post-issuance :inspection.template.kind.phase/post-issuance))

(defn dependent-inspection-names [template-kind-id lang]
  (->> (<sub [::dependent-template-kinds template-kind-id])
       (map #(inspection-model/inspection-template-kind-name % lang))
       (remove str/blank?)
       sort
       (str/join ", ")))

(defn save-template-kind-phases [id phases phase selected?]
  (let [new-phases ((if selected? conj disj) phases phase)]
    (>evt [::set-kind-value id :inspection.template.kind/phases new-phases])
    (when (seq new-phases)
      (>evt [::save-value-kind id :inspection.template.kind/phases new-phases]))))

(defn template-kind-phase-checkbox [{:keys [tr id phase phases dependent-names]}]
  (r/with-let [*pending? (r/atom false)
               close    #(reset! *pending? false)]
    (let [selected? (contains? phases phase)]
      [:<>
       (cc/checkbox
         {:id         (str id "-" (name phase) "-phase-checkbox")
          :label-text (tr [(template-kind-phase-label-key phase)])
          :checked    selected?
          :on-change  #(if (and (not %) (seq dependent-names))
                         (reset! *pending? true)
                         (save-template-kind-phases id phases phase %))})
       [ui/confirm-modal
        {:id              (str id "-" (name phase) "-dependency-phase-confirmation")
         :tr              tr
         :open?           @*pending?
         :on-close        close
         :on-yes-click    #(do
                             (save-template-kind-phases id phases phase false)
                             (close))
         :modal-title     (tr [:are-you-sure])
         :yes-button-text (tr [:yes])}
        (tr [:inspection.template.kind/phase-dependency-confirmation] [dependent-names])]])))

(defn template-kind-phases-selector [tr {:db/keys [id] :as item}]
  (let [phases          (or (:inspection.template.kind/phases item) #{})
        lang            (<sub [:language])
        dependent-names (dependent-inspection-names id lang)]
    [uic/form-field-wide
     [:fieldset.bx--fieldset
      [:legend.bx--label (tr [:inspection.template.kind/phases])]
      [:div.flex.flex-wrap.gap-4.justify-start
       (for [phase template-kind-phase-options]
         ^{:key phase}
         [:div
          [template-kind-phase-checkbox {:tr              tr
                                         :id              id
                                         :phase           phase
                                         :phases          phases
                                         :dependent-names dependent-names}]])]]]))

(defn template-kind-visible-to-applicant-selector [tr {:db/keys [id] :as item} save-value-fn]
  (let [visible? (inspection-model/template-kind-visible-to-applicant? item)]
    [uic/form-field-wide
     [:div (cc/checkbox
             {:id         (kind-editor-component-id id "visible-to-applicant-checkbox")
              :label-text (tr [:inspection.template.kind/visible-to-applicant?])
              :checked    visible?
              :on-change  #(save-value-fn :inspection.template.kind/visible-to-applicant? %)})]
     (when visible?
       [:div.mt-2.text-xs (tr [:inspection.template.kind/visible-to-applicant-help])])]))

(rf/reg-event-fx ::add-template-kind-dependency.success
  (fn [_ [_ status changes]]
    {:dispatch [::entities/apply-changes status changes]}))

(rf/reg-event-db ::clear-selected-dependencies
  (fn [db [_ template-kind-id]]
    (-> db
        (assoc-in [::selected-dependency-template-kind template-kind-id] nil)
        (assoc-in [::selected-dependency-visit-results template-kind-id] #{}))))

(declare stakeholder-emails-editor)

(defn end-visit-notification-settings
  [tr
   {:db/keys                       [id]
    :inspection.template.kind/keys [visit-completion-notifies? end-visit-notification-recipients end-visit-notification-for-results]}]
  (let [lang              (<sub [:language])
        get-component-id  (partial kind-editor-component-id id)
        save-value-fn     (partial save-kind-attr id)
        all-visit-results (template-kind-visit-results (<sub [::current-kind]))
        selected-results  (<sub [::get-kind-value :inspection.template.kind/end-visit-notification-for-results])]
    [uic/form-field-wide
     [:div.flex.flex-wrap.gap-4.justify-start
      [:div (cc/checkbox
              {:id         (get-component-id "visit-completion-notifies-checkbox")
               :label-text (tr [:inspection.template.kind/visit-completion-notifies?])
               :checked    (boolean visit-completion-notifies?)
               :on-change  #(save-value-fn :inspection.template.kind/visit-completion-notifies? %)})]]
     (when visit-completion-notifies?
       [:div.flex-col.mt-5
        [:div.flex-1
         ^{:key (get-component-id (str "end-visit-notification-for-results-" (hash selected-results)))}
         [ui-mst/multi-select-tags
          {:values      all-visit-results
           :value       (or selected-results
                            end-visit-notification-for-results)
           :val->str    #(inspection-model/visit-result-name % lang)
           :on-change   #(let [results (some->> % (keep :db/id) set)]
                           (>evt [::set-kind-value id :inspection.template.kind/end-visit-notification-for-results %])
                           (>evt [::save-value-kind id :inspection.template.kind/end-visit-notification-for-results results]))
           :label-text  (tr [:inspection.template.kind/visit-completion-notifies-for-results])
           :select-text (tr [:inspection.template.kind/select-visit-results])
           :tr          tr}]]
        [:div.mt-4
         [stakeholder-emails-editor tr id end-visit-notification-recipients :inspection.template.kind/end-visit-notification-recipients
          (get-component-id "end-visit-notification-recipients")]]])]))

(rf/reg-event-fx ::add-template-kind-dependency
  (fn [{:keys [db]} [_ template-kind-id authority-group-id]]
    (let [selected-template-kind (get-in db [::selected-dependency-template-kind template-kind-id])
          selected-results       (get-in db [::selected-dependency-visit-results template-kind-id])
          mandatory-results      (->> (template-kind-visit-results selected-template-kind)
                                      (filter #(inspection-model/visit-result-rule-set? % :inspection.visit.result-rule/close-inspection)))
          all-results            (set/union (set selected-results) (set mandatory-results))]
      {:comm/command {:command          :inspection/add-template-kind-dependency
                      :data             {:authority-group-id          authority-group-id
                                         :inspection-template-kind-id template-kind-id
                                         :required-template-kind-id   (:db/id selected-template-kind)
                                         :required-visit-result-ids   (set (map :db/id all-results))}
                      :on-success       [::add-template-kind-dependency.success]
                      :finally-dispatch [::clear-selected-dependencies template-kind-id]}})))

(defn stakeholder-emails-editor [tr kind-id recipients attr component-id]
  [text-editor/emails-editor
   {:id           component-id
    :recipients   recipients
    :invalid-text (tr [:inspection.template.kind/stakeholder-notification-recipients-mismatch])
    :label-text   (tr [:inspection.template.kind/stakeholder-notification-recipients])
    :value-fn     #(<sub [::get-kind-value attr])
    :set-fn       #(>evt [::set-kind-value kind-id attr %])
    :save-fn      #(let [emails (vec %)]
                     (>evt [::set-kind-value kind-id attr emails])
                     (>evt [::save-value-kind kind-id attr emails]))}])


(defn confirm-notification-settings
  [tr
   {:db/keys                       [id]
    :inspection.template.kind/keys [confirm-notification-recipients first-confirmation-notifies? any-confirmation-notifies?]}]
  (let [get-component-id (partial kind-editor-component-id id)
        save-value-fn    (partial save-kind-attr id)]
    [uic/form-field-wide
     [:div.flex.flex-wrap.gap-4.justify-start
      [:div (cc/checkbox
              {:id         (get-component-id "first-confirmation-notifies-checkbox")
               :label-text (tr [:inspection.template.kind/first-confirmation-notifies?])
               :checked    (boolean first-confirmation-notifies?)
               :on-change  #(do
                              (when-not %
                                (save-value-fn :inspection.template.kind/any-confirmation-notifies? false))
                              (save-value-fn :inspection.template.kind/first-confirmation-notifies? %))})]
      (when first-confirmation-notifies?
        [:div (cc/checkbox
                {:id         (get-component-id "any-confirmation-notifies-checkbox")
                 :label-text (tr [:inspection.template.kind/any-confirmation-notifies?])
                 :checked    (boolean (and first-confirmation-notifies? any-confirmation-notifies?))
                 :on-change  (partial save-value-fn :inspection.template.kind/any-confirmation-notifies?)})])]
     (when first-confirmation-notifies?
       [:div.flex-row.mt-5
        [stakeholder-emails-editor tr id confirm-notification-recipients :inspection.template.kind/confirm-notification-recipients
         (get-component-id "confirm-notification-recipients")]])]))

(defn kind-editor [{:keys [tr authority-group-id] :as props}
                   {:db/keys                       [id]
                    :inspection.template.kind/keys [recurring? recurrence-interval triggers-refunds?
                                                    prevent-request-when-unpaid? multiple-applicant-bookings? visit-type]
                    :as                            item}
                   _]
  (let [lang                       (<sub [:language])
        ctx                        {:tr tr :id id :item item :required? true}
        integration?               (<sub [::gas/has-feature? :group.feature/backend-system-integration])
        get-component-id           (partial kind-editor-component-id id)
        save-value-fn              (partial save-kind-attr id)
        stakeholder-notifications? (<sub [::ff/enabled? :feature-flag.id/inspection-stakeholder-notifications])
        ff-unpaid-enabled?         (<sub [::ff/enabled? :feature-flag.id/prevent-request-when-unpaid])
        permit-types               (<sub [::gas/group-permit-types])
        primary-permit-type        (<sub [::gas/group-primary-permit-type])
        multiple-bookings?         (<sub [::gas/has-feature? :group.feature/multiple-inspection-bookings-applicant])
        inspection-dependencies?   (<sub [::ff/enabled? :feature-flag.id/inspection-dependencies])
        pre-issuance-inspections?  (<sub [::ff/enabled? :feature-flag.id/pre-issuance-inspections])]
    [:<>
     [ui-i18n/edit-language-selector {:allowed-locales-sub ::gas/allowed-locales}]
     [uic/form-field-wide
      [i18n-editor-for-kind ctx :inspection.template.kind/names]]
     (when integration?
       [uic/form-field-wide
        [simple-editor-for-kind (assoc ctx :required? false) ui/text-input :inspection.template.kind/external-code]])
     [visit-type-selector props visit-type save-value-fn get-component-id lang]
     (when pre-issuance-inspections?
       [template-kind-phases-selector tr item])
     (when (and pre-issuance-inspections?
                (not (ptc-model/permit-types-without-applicant-visible-inspections primary-permit-type)))
       [template-kind-visible-to-applicant-selector tr item save-value-fn])
     (when (<sub [::gas/has-feature? :group.feature/recurring-inspections])
       [:<>
        [uic/form-field-wide
         (cc/checkbox
           {:id         (get-component-id "recurring-checkbox")
            :label-text (tr [:inspection.template.kind/recurring?])
            :checked    (boolean recurring?)
            :on-change  #(save-value-fn :inspection.template.kind/recurring? %)})]
        (when recurring?
          [uic/form-field-wide
           [ui-select/select
            {:values       inspection-model/inspection-recurrence-options
             :id           (get-component-id "recurrence-interval-select")
             :label-text   (tr [:inspection.template.kind/recurrence-interval])
             :val->str     #(if (= 1 %)
                              (tr [:select/a-month])
                              (tr [:select/n-months] [%]))
             :on-change    #(save-value-fn :inspection.template.kind/recurrence-interval %)
             :value        recurrence-interval
             :allow-empty? false}]])])
     [uic/form-field-wide
      [i18n-editor-for-kind (assoc ctx :multiline? true) :inspection.template.kind/descriptions]]
     (when (empty? (set/intersection ptc-model/permit-types-without-refunds permit-types))
       [uic/form-field-wide
        [:div.flex.flex-wrap.gap-4.justify-start
         [:div (cc/checkbox
                 {:id         (get-component-id "bond-refund-checkbox")
                  :label-text (tr [:inspection.template.kind/triggers-refunds?])
                  :checked    (boolean triggers-refunds?)
                  :on-change  #(save-value-fn :inspection.template.kind/triggers-refunds? %)})]
         (when ff-unpaid-enabled?
           [:div (cc/checkbox
                   {:id         (get-component-id "prevent-request-when-unpaid-checkbox")
                    :label-text (tr [:inspection.template.kind/prevent-request-when-unpaid?])
                    :checked    (boolean prevent-request-when-unpaid?)
                    :on-change  #(save-value-fn :inspection.template.kind/prevent-request-when-unpaid? %)})])]])

     (when stakeholder-notifications?
       [:<>
        [:h5.mt-5 (tr [:inspection.template.kind/stakeholder-emails])]
        [confirm-notification-settings tr item]
        [end-visit-notification-settings tr item]])

     (when (and id inspection-dependencies?)
       [:<>
        [inspection-dependencies-add
         {:tr                 tr
          :template-kind-id   id
          :authority-group-id authority-group-id}]
        [inspection-dependencies-list
         {:tr                 tr
          :template-kind-id   id
          :authority-group-id authority-group-id}]])

     (when multiple-bookings?
       [uic/form-field-wide
        (cc/checkbox
          {:id         (get-component-id "multi-booking-checkbox")
           :label-text (tr [:inspection.template.kind/multiple-applicant-bookings?])
           :checked    (boolean multiple-applicant-bookings?)
           :on-change  #(save-value-fn :inspection.template.kind/multiple-applicant-bookings? %)})])

     (when id
       [qualification-selector-v2 tr item save-value-fn])

     (when id
       [:div.mt-4
        [ui/modal-confirmation-button
         {:trigger-button-opts {:kind :tertiary
                                :icon i/trash}
          :trigger-button-text (tr [:inspection.template/remove-template-kind])
          :on-yes-click        #(>evt [::save-value-kind id :domain/state :inspection.template.kind.state/retired])
          :tr                  tr
          :id                  "delete-inspection-template-kind"}
         (tr [:inspection.template/remove-template-kind-desc]
             [(inspection-model/inspection-template-kind-name item lang)])]])

     (when (<sub [::no-templates-for-template-kind id])
       [ui/warning
        (tr [:inspection.template/no-templates-specified-for-kind])])

     (when (= id temp-id)
       [uic/form-field-wide
        (cc/button
          {:icon             i/save
           :icon-description (tr [:save])
           :on-click         #(>evt [::save-new-kind authority-group-id item])
           :disabled         (<sub [::save-new-template-disabled?])}
          (tr [:save]))])]))

(defn template-kind-description [template-kind language tr]
  (let [description              (-> template-kind
                                     :inspection.template.kind/descriptions
                                     (i18n-model/get-text-from-entities-or-map language)
                                     (or (:inspection.template.kind/description template-kind))
                                     ui/summarize-description)
        has-deps?                (<sub [::inspection-template-kind-has-dependencies? (:db/id template-kind)])
        inspection-dependencies? (<sub [::ff/enabled? :feature-flag.id/inspection-dependencies])]
    (if (and has-deps? inspection-dependencies?)
      [:div.flex.items-center.gap-2
       [:span description]
       [:span.text-amber-500
        (cc/icon {:icon        i/warning
                  :description (tr [:inspection.dependencies/has-dependencies])
                  :aria-hidden false})]]
      description)))

(defn template-kinds-table [{:keys [authority-group-id wrapped-editor]}]
  #_{:clj-kondo/ignore [:deprecated-var]}
  (let [language (<sub [:language])
        tr       (<sub [:tr])
        kinds    (<sub [::template-kinds])]
    [ui-table/table-2
     {:id                (str "template-kind-table-" authority-group-id)
      :items             kinds
      :header+extractors (concat [[:inspection.template.kind/names
                                   #(inspection-util/template-kind->name % language)]
                                  [:inspection.template.kind/descriptions
                                   #(template-kind-description % language tr)]]
                                 (when (<sub [::gas/has-feature? :group.feature/backend-system-integration])
                                   [:inspection.template.kind/external-code]))
      :expand-component  wrapped-editor
      :on-expand         #(>evt [::select-kind-for-edit kinds (:db/id %)])
      :expanded?         (comp (partial = (<sub [::id-of-current-kind])) :db/id)
      :tr                tr
      :on-reorder        (fn [from to]
                           (>evt [::reorder
                                  authority-group-id
                                  (dnd/ordering-tx-for-entity from to)]))}]))

(defn template-kinds-table-v2 [{:keys [authority-group-id wrapped-editor]}]
  (let [language (<sub [:language])
        tr       (<sub [:tr])
        kinds    (<sub [::template-kinds])]
    [ui-table/modern-responsive-table
     {:id                (str "template-kind-table-" authority-group-id)
      :cols              (concat [[:inspection.template.kind/names
                                   #(inspection-util/template-kind->name % language)]
                                  [:inspection.template.kind/descriptions
                                   #(template-kind-description % language tr)]]
                                 (when (<sub [::gas/has-feature? :group.feature/backend-system-integration])
                                   [[:inspection.template.kind/external-code
                                     :inspection.template.kind/external-code]]))
      :expanded-func     wrapped-editor
      :expanded-row-keys (<sub [::expanded-kinds])
      :on-expand         (fn [kind-id]
                           (>evt [::select-kind-for-edit kinds kind-id]))
      :tr                tr
      :items             kinds
      :on-reorder        (fn [from to]
                           (>evt [::reorder
                                  authority-group-id
                                  (dnd/ordering-tx-for-entity from to)]))}]))

(defn view [{:keys [tr authority-group-id] :as ctx}]
  (let [wrapped-editor     (partial kind-editor ctx)
        multiple-bookings? (<sub [::gas/has-feature? :group.feature/multiple-inspection-bookings-applicant])]
    [acommon/section
     {}
     (if (<sub [::ff/enabled? :feature-flag.id/inspection-type-table-v2])
       [template-kinds-table-v2 (util/mmap authority-group-id wrapped-editor)]
       [template-kinds-table (util/mmap authority-group-id wrapped-editor)])
     (when (not (<sub [::adding-new?]))
       [ui-table/add-new-button
        {:tr         tr
         :class-name "mt-3"
         :on-click   #(>evt [::add-new-template-kind (<sub [::order-for-last]) multiple-bookings?])}
        :inspection.template/create])]))
